import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, AsOfPicker, fmtMoney0, fmtMoney2, fmtPct, todayIso } from "./_shared";

export default function FinanceCapital() {
  const [months, setMonths] = useState(24);
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/finance/v2/capital-series", { params: { months, as_of: asOf }});
        setData(data);
      } catch (e) { toast.error("Load failed: " + formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [months, asOf]);

  if (loading && !data) return <div className="p-10 text-[#6B7280]" data-testid="capital-loading">Loading…</div>;
  if (!data) return null;

  const series = data.series || [];
  const maxAny = Math.max(1, ...series.map(s => Math.max(s.deployed, s.collected, s.ending_outstanding)));

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="finance-capital">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance V2 · preview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Capital & Recycling</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">Monthly deployed vs collected · {data.months_window} month window</div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Window</label>
            <select value={months} onChange={e => setMonths(+e.target.value)} data-testid="capital-window"
                    className="h-9 px-2 text-sm border border-[#E5E7EB] rounded-md bg-white">
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
              <option value={60}>60 months</option>
            </select>
          </div>
          <AsOfPicker value={asOf} onChange={setAsOf} testid="capital-as-of" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Kpi label="Peak outstanding" value={fmtMoney0(data.peak_outstanding.amount)}
             sub={data.peak_outstanding.month ? `Reached in ${data.peak_outstanding.month}` : ""}
             accent testid="capital-peak" />
        <Kpi label="Capital recycling ratio (lifetime)" value={fmtPct(data.capital_recycling_ratio_lifetime)}
             sub="cum_collected / cum_deployed"
             testid="capital-recycling-ratio" />
        <Kpi label="Ending outstanding" value={fmtMoney0(series.at(-1)?.ending_outstanding)}
             sub="Contractual approximation (see notes)"
             testid="capital-ending-outstanding" />
      </div>

      <Section title="Monthly series" category="mixed">
        <div className="overflow-auto rounded-md border border-[#E5E7EB] bg-white">
          <table className="w-full text-xs" data-testid="capital-series-table">
            <thead className="bg-[#F9FAFB] sticky top-0">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Month</th>
                <th className="px-3 py-2 font-medium text-right">Deployed</th>
                <th className="px-3 py-2 font-medium text-right">Collected</th>
                <th className="px-3 py-2 font-medium text-right">Cum deployed</th>
                <th className="px-3 py-2 font-medium text-right">Cum collected</th>
                <th className="px-3 py-2 font-medium">Ending outstanding</th>
              </tr>
            </thead>
            <tbody>
              {series.map(row => (
                <tr key={row.month} className="border-b border-[#F3F4F6]" data-testid={`capital-row-${row.month}`}>
                  <td className="px-3 py-2 font-mono">{row.month}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1E40AF]">{row.deployed ? fmtMoney0(row.deployed) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#065F46]">{row.collected ? fmtMoney0(row.collected) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{fmtMoney0(row.cum_deployed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{fmtMoney0(row.cum_collected)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 bg-[#F3F4F6] rounded flex-1 max-w-[200px]">
                        <div className="h-2 bg-[#0066FF] rounded" style={{ width: `${(row.ending_outstanding / maxAny) * 100}%` }} />
                      </div>
                      <span className="tabular-nums text-[11px] font-medium">{fmtMoney0(row.ending_outstanding)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-[#6B7280] mt-2">
          {(data.notes || []).map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      </Section>
    </div>
  );
}
